/**
 * 重要事業の一貫性保持ユーティリティ
 */

export interface ImportantBusiness {
  id: string;
  name: string;
  reason: string;
}

export interface FormData {
  [key: string]: string;
}

const normalizeLineBreaks = (input: string): string =>
  input.replace(/\r\n/g, '\n').replace(/\r/g, '\n');

const detectLineBreak = (input: string): string =>
  input.includes('\r\n') ? '\r\n' : '\n';

/**
 * Phase 1のテキストから重要事業を抽出し、最大2件に制限
 * 多行の理由にも対応
 */
export const extractImportantBusinesses = (
  text: string
): ImportantBusiness[] => {
  if (!text) return [];

  const normalizedText = normalizeLineBreaks(text);

  // 多行対応: 理由欄に改行や箇条書きが含まれる場合に対応
  // 次の【重要事業】または文末まで非貪欲にマッチ
  const regex =
    /【重要事業(\d+)：([^】]+)】[\t 　]*\n[\t 　]*理由：([\s\S]*?)(?=\n[\t 　]*【重要事業\d+：|$)/g;
  const businesses: ImportantBusiness[] = [];
  let match;

  while ((match = regex.exec(normalizedText)) !== null) {
    businesses.push({
      id: match[1],
      name: match[2].trim(),
      reason: match[3].trim(),
    });
  }

  // 最大2件に制限
  return businesses.slice(0, 2);
};

/**
 * 重要事業名のリストを取得
 */
export const getBusinessNames = (
  businesses: ImportantBusiness[]
): string[] => {
  return businesses.map((b) => b.name);
};

/**
 * フィールドのテンプレート(各フィールドの説明部分)
 */
const getFieldTemplate = (fieldId: string): string => {
  const templates: Record<string, string> = {
    human_resources: '{具体的な内容}',
    facility_recovery: '{具体的な内容}',
    it_recovery: '{具体的な内容}',
    supply_chain: '{具体的な内容}',
    backup_location: '{具体的な内容}',
    remote_work: '{具体的な内容}',
    external_partners: '{具体的な内容}',
  };
  return templates[fieldId] || '{具体的な内容}';
};

/**
 * テキスト内の重要事業名を更新し、重要事業数の増減にも対応
 * 事業名でマッチングすることで、順番が入れ替わっても内容を正しく保持
 */
export const updateBusinessNamesInText = (
  text: string,
  businesses: ImportantBusiness[],
  fieldId: string
): string => {
  const newline = detectLineBreak(text);
  const normalizedText = normalizeLineBreaks(text);

  if (!normalizedText.trim() || businesses.length === 0) {
    // 空の場合は新しいテンプレートを生成
    const newBlocks: string[] = [];
    businesses.forEach((business, index) => {
      const businessNum = index + 1;
      const businessHeader = `【重要事業${businessNum}：${business.name}】`;
      const content = getFieldTemplate(fieldId);
      newBlocks.push(`${businessHeader}${newline}${content}`);
    });
    return newBlocks.join(`${newline}${newline}`);
  }

  // 既存の重要事業ブロックを抽出(事業名をキーにする)
  const existingBlocksByName: Map<string, string> = new Map();
  const existingBlocksByNum: Map<number, string> = new Map();
  const blockRegex =
    /【重要事業(\d+)：([^】]+)】[\t 　]*\n([\s\S]*?)(?=\n[\t 　]*【重要事業\d+：|$)/g;
  let match;

  while ((match = blockRegex.exec(normalizedText)) !== null) {
    const businessNum = parseInt(match[1], 10);
    const businessName = match[2].trim();
    const content = match[3].trim();

    // 事業名と番号の両方でマップに格納
    existingBlocksByName.set(businessName, content);
    existingBlocksByNum.set(businessNum, content);
  }

  // 新しいテキストを構築
  const newBlocks: string[] = [];
  businesses.forEach((business, index) => {
    const businessNum = index + 1;
    const businessHeader = `【重要事業${businessNum}：${business.name}】`;

    // コンテンツの取得優先順位:
    // 1. 事業名でマッチング(順番が入れ替わっても内容を保持)
    // 2. 番号でマッチング(新しい事業の場合)
    // 3. テンプレート(どちらもない場合)
    let content = existingBlocksByName.get(business.name);

    if (!content) {
      // 事業名でマッチしない場合は、番号でマッチを試みる
      content = existingBlocksByNum.get(businessNum);
    }

    if (
      !content ||
      content === '{事業名}' ||
      content === getFieldTemplate(fieldId)
    ) {
      content = getFieldTemplate(fieldId);
    }

    const normalizedContent = normalizeLineBreaks(content);
    const adjustedContent =
      newline === '\n'
        ? normalizedContent
        : normalizedContent.replace(/\n/g, newline);

    newBlocks.push(`${businessHeader}${newline}${adjustedContent}`);
  });

  return newBlocks.join(`${newline}${newline}`);
};

/**
 * Phase 2/3のフィールドを更新する共通処理
 * handleImportantBusinessChange と useEffect の両方から呼び出せるようにする
 */
export const computeUpdatedFields = (
  currentFormData: FormData,
  businesses: ImportantBusiness[]
): Record<string, string> => {
  const phase2Fields = [
    'human_resources',
    'facility_recovery',
    'it_recovery',
    'supply_chain',
  ];

  const phase3Fields = ['backup_location', 'remote_work', 'external_partners'];

  const allFields = [...phase2Fields, ...phase3Fields];
  const updatedFields: Record<string, string> = {};

  allFields.forEach((fieldId) => {
    const currentValue = currentFormData[fieldId] || '';
    updatedFields[fieldId] = updateBusinessNamesInText(
      currentValue,
      businesses,
      fieldId
    );
  });

  return updatedFields;
};
